// KI-Formulierungshilfe je Position/Titel in der Angebotsmaske (Wunsch Malermeister):
// reine, testbare Logik ohne React/Netzwerk. Der eigentliche Provider-Aufruf läuft
// über den BESTEHENDEN Endpunkt POST /api/ai/rewrite (kein zweiter Rewrite-Pfad).
//
// Die drei Verteidigungslinien dieses Features:
// 1. STRUKTURELL: buildPositionAiTextUpdate() kann ausschließlich die Textfelder
//    'beschreibung'/'longText' erzeugen — Menge, Einheit, Preis und Positionsnummer
//    sind auf diesem Weg technisch unerreichbar (nicht "das Modell macht das schon").
// 2. SERVERSEITIG: /api/ai/rewrite wendet applyPdfAiSafeguards an (Reparatur).
// 3. CLIENTSEITIG (diese Datei): reviewAiTextProposal() prüft den Vorschlag NACH der
//    Antwort gegen das Original — fehlende kritische Pflichtangaben (Fabrikat,
//    "oder gleichwertig") lehnen die Übernahme ab, fehlende technische Angaben
//    (DIN-Normen, Maße/Spannungen) und Zahl-/Mengenangaben warnen sichtbar.
//
// Übernommen wird NIE automatisch: der Nutzer sieht Vorher/Nachher und entscheidet.
// Bei "Verwerfen" wird nichts geschrieben — das Original bleibt zeichengleich.

import {
  AI_CRITICAL_LINE_PATTERNS,
  normalizeAiLine,
} from './ai-text-safeguards';

// ---------------------------------------------------------------------------
// Erlaubte Zielfelder (strukturelle Absicherung)
// ---------------------------------------------------------------------------

/**
 * Die KI-Formulierungshilfe bearbeitet ausschließlich die beiden Prosa-Felder
 * einer Position: 'beschreibung' (Standard-Drucktext im LV) und 'longText'
 * (GAEB-Langtext). Bewusst NICHT dabei: name (Kurztext/Bezeichnung — dient als
 * Zeilen-Label und Rechnungs-Titel), menge, einheit, preis, positionNumber.
 */
export const POSITION_AI_TEXT_FIELDS = ['beschreibung', 'longText'] as const;

export type PositionAiTextField = (typeof POSITION_AI_TEXT_FIELDS)[number];

export type PositionAiTextUpdate =
  | { beschreibung: string }
  | { longText: string };

/**
 * Baut das Update-Objekt für den Store-Aufruf. Wirft für jedes andere Feld —
 * damit kann dieser Pfad strukturell nichts außer Text anfassen, egal was die
 * KI (oder ein Programmierfehler) liefert.
 */
export function buildPositionAiTextUpdate(
  field: PositionAiTextField,
  text: string,
): PositionAiTextUpdate {
  if (field === 'beschreibung') {
    return { beschreibung: text };
  }
  if (field === 'longText') {
    return { longText: text };
  }
  throw new Error(
    `KI-Textübernahme für Feld "${String(field)}" ist nicht erlaubt — zulässig sind nur: ${POSITION_AI_TEXT_FIELDS.join(', ')}.`,
  );
}

// ---------------------------------------------------------------------------
// Vorlagen-Befehle
// ---------------------------------------------------------------------------

export interface PositionAiVorlage {
  id: string;
  label: string;
  instruction: string;
}

/**
 * Vorlagen für die häufigsten Befehle. "VOB" bewusst als STIL-Vorlage formuliert
 * ("VOB-typische Ausschreibungssprache"), nicht als "VOB-konform": wir prüfen
 * keine Rechtskonformität und dürfen sie deshalb auch nicht behaupten.
 */
export const POSITION_AI_VORLAGEN: readonly PositionAiVorlage[] = [
  {
    id: 'ausfuehrlicher',
    label: 'Ausführlicher formulieren',
    instruction:
      'Formuliere den Leistungstext ausführlicher und vollständig ausformuliert. '
      + 'Erfinde keine neuen Leistungen, Materialien oder Qualitäten und lasse keine bestehenden Angaben weg.',
  },
  {
    id: 'knapper',
    label: 'Knapper fassen',
    instruction:
      'Fasse den Leistungstext knapper und präziser. Alle inhaltlichen Angaben '
      + '(Leistungsumfang, Materialien, Arbeitsschritte, Mengen-, Maß-, Norm- und Fabrikatsangaben) müssen erhalten bleiben.',
  },
  {
    id: 'professioneller',
    label: 'Sprachlich professioneller',
    instruction:
      'Formuliere den Leistungstext sprachlich professioneller und einheitlicher, '
      + 'ohne den Leistungsumfang oder technische Angaben zu verändern.',
  },
  {
    id: 'rechtschreibung',
    label: 'Rechtschreibung & Grammatik',
    instruction:
      'Korrigiere ausschließlich Rechtschreibung, Grammatik und Zeichensetzung. '
      + 'Verändere Inhalt, Reihenfolge und Fachbegriffe nicht.',
  },
  {
    id: 'vob-stil',
    label: 'Formulierung nach VOB-Muster',
    instruction:
      'Formuliere den Text im Stil VOB-typischer Ausschreibungs- und Leistungstexte: '
      + 'sachlich, leistungsbeschreibend, ohne werbliche Formulierungen. Der Leistungsumfang darf sich nicht ändern. '
      + 'Dies ist eine Formulierungshilfe, keine rechtliche Prüfung auf VOB-Konformität.',
  },
];

/** Ehrlicher Hinweis, der im Dialog steht (ein Satz, Anforderung "keine Rechtssicherheit verkaufen"). */
export const POSITION_AI_DISCLAIMER =
  'Formulierungshilfe — keine rechtliche Prüfung (auch nicht auf VOB-Konformität). '
  + 'Menge, Einheit, Preis und Positionsnummer werden nicht verändert.';

// ---------------------------------------------------------------------------
// Nachprüfung des KI-Vorschlags gegen das Original
// ---------------------------------------------------------------------------

export interface PositionAiReview {
  /**
   * 'abgelehnt'  → kritische Pflichtangabe fehlt (Fabrikat / "oder gleichwertig"):
   *                Übernahme wird blockiert.
   * 'warnung'    → technische Angabe (DIN, Maße/Spannungen) oder Zahl-/Mengenangabe
   *                fehlt wörtlich: Übernahme möglich, aber sichtbar gewarnt.
   * 'ok'         → keine geschützte Angabe verloren.
   */
  verdict: 'ok' | 'warnung' | 'abgelehnt';
  fehlendeKritischeAngaben: string[];
  fehlendeTechnischeAngaben: string[];
  fehlendeZahlAngaben: string[];
}

// ---------------------------------------------------------------------------
// GRANULARITÄT: geprüft wird die TATSACHE, nicht die Zeile.
//
// Die erste Fassung dieses Wächters verlangte, dass die ganze Zeile, die ein
// Schutzmuster trifft, normalisiert im Vorschlag vorkommt. Am einzeiligen
// Normalfall (eine Positionsbeschreibung ist EINE Zeile) ist die "Zeile" der
// ganze Satz — jede Umformulierung ändert ihn, also galt die Angabe IMMER als
// verloren: gemessen 5 von 5 Vorschlägen "abgelehnt", darunter einer, der alle
// Angaben wörtlich behielt. Ein Wächter, der nicht unterscheidet, ist wertlos:
// der Nutzer sieht nur noch rot und hält die KI für kaputt.
//
// Deshalb wird jetzt je Tatsache geprüft, jeweils auf der Granularität, auf der
// die Tatsache lebt — grob genug, dass Umformulieren erlaubt bleibt, fein genug,
// dass Verlust auffällt:
//   • Fabrikat/Typ      → der NAME (Wort-Token), nicht der Satz. Der Name ist die
//                         Tatsache; der Satz drumherum ist genau das, was der
//                         Nutzer ändern lassen wollte.
//   • "oder gleichwertig" → die WENDUNG (Stamm-Match, damit "oder gleichwertiges
//                         Fabrikat" auch zählt). VOB-Gleichwertigkeitsvorbehalt:
//                         fällt er weg, wird aus einem offenen ein produkt-
//                         gebundenes Leistungssoll — teuerster Einzelverlust.
//   • Normen            → die KENNUNG ("DIN EN 13300"), trennzeichen-tolerant
//                         verglichen ("DIN-EN 13300" zählt).
//   • Technische Werte  → das TOKEN ("24V", "E30", "IP 44").
//   • Zahlen/Zahlwörter → Token-basiert wie bisher (fand "zweimal → mehrfach").
// ---------------------------------------------------------------------------

const ZAHL_TOKEN_PATTERN = /\d+(?:[.,]\d+)?/g;
const ZAHLWORT_PATTERN =
  /\b(?:ein|zwei|drei|vier|fünf|sechs|sieben|acht|neun|zehn)(?:mal|fach|lagig)\b/gi;

/** VOB-Gleichwertigkeitsvorbehalt; Stamm-Match erlaubt "gleichwertiger/-es". */
const GLEICHWERTIG_PATTERN = /\boder\s+gleichwertig/i;

/** Fabrikats-/Typangabe samt Wert bis zum nächsten Trenner. */
const FABRIKAT_PATTERN =
  /\b(?:Fabrikat\s+der\s+Planung|Angebotenes\s+Fabrikat|Angebotener\s+Typ|Fabrikat|Hersteller|Typ)\s*:?\s*([^,;.\n]+)/gi;

/** Normkennungen. Längere Präfixe zuerst, sonst schluckt "DIN" das "DIN EN". */
const NORM_PATTERN =
  /\b(?:DIN\s*EN\s*ISO|DIN\s*EN|DIN\s*ISO|DIN\s*VDE|DIN|EN|ISO|VDE)\s*\d[\d.\-:/]*/gi;

/** Technische Werte, die als Token überleben müssen (Spannungen, Maße, Klassen). */
const TECH_TOKEN_PATTERN =
  /\b(?:\d+(?:[.,]\d+)?\s*(?:kV|kW|mA|Ah|Hz|°C|mm|cm|m²|V|W|A)|E30|E90|IP\s*\d{2})\b/gi;

/** Wörter, die im Fabrikatswert kein Produktname sind. */
const FABRIKAT_STOPWORDS = new Set([
  'der', 'die', 'das', 'des', 'dem', 'den', 'und', 'oder', 'gleichwertig',
  'gleichwertiger', 'gleichwertiges', 'gleichwertigem', 'planung', 'fabrikat',
  'hersteller', 'typ', 'art', 'bauart', 'ausführung', 'ausfuehrung', 'qualität',
  'qualitaet', 'nach', 'gemäß', 'gemaess', 'sowie', 'bzw', 'ähnlich', 'aehnlich',
  'siehe', 'anlage', 'wahl', 'auswahl', 'angabe', 'angebot', 'liste',
]);

function uniqueMatches(text: string, pattern: RegExp): string[] {
  const matches = text.match(pattern) ?? [];
  return Array.from(new Set(matches.map((m) => m.toLowerCase())));
}

/** Vergleichsform ohne Trennzeichen: "DIN-EN 13300" === "DIN EN 13300". */
const stripSeparators = (value: string) => value.toLowerCase().replace(/[^a-z0-9äöüß]/g, '');

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Token-Treffer mit linker Wortgrenze: "Esser" darf NICHT von "besser" erfüllt
 * werden (Fehlerrichtung "übersehen"). Rechts bewusst offen, damit Beugungen und
 * Komposita zählen ("Brillux-Farbe", "Brillux®") — Fehlerrichtung "falscher Alarm".
 */
function containsToken(haystackLower: string, token: string): boolean {
  return new RegExp(`(?<![a-z0-9äöüß])${escapeRegExp(token.toLowerCase())}`, 'i').test(haystackLower);
}

interface FabrikatsAngabe {
  /** Gesamter erkannter Wert, für die Meldung an den Nutzer ("Brillux"). */
  wert: string;
  /** Die Tokens, die im Vorschlag überleben müssen. */
  tokens: string[];
}

function extractFabrikatsAngaben(text: string): FabrikatsAngabe[] {
  const angaben: FabrikatsAngabe[] = [];
  const pattern = new RegExp(FABRIKAT_PATTERN.source, 'gi');
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    // Wert endet spätestens vor dem Gleichwertigkeitsvorbehalt ("Brillux oder gleichwertig").
    const wert = match[1].split(/\s+oder\s+/i)[0].trim();
    if (!wert) continue;

    const tokens = (wert.match(/[A-Za-zÄÖÜäöüß0-9][A-Za-zÄÖÜäöüß0-9.\-]*/g) ?? []).filter((token) => {
      const lower = token.toLowerCase();
      if (FABRIKAT_STOPWORDS.has(lower)) return false;
      // Produktnamen sind großgeschrieben oder tragen Ziffern; alles andere
      // (kleingeschriebene Gattungswörter) wäre eine Quelle falscher Alarme.
      if (!/^[A-ZÄÖÜ0-9]/.test(token)) return false;
      return /\d/.test(token) || token.length >= 3;
    });

    if (tokens.length > 0) {
      angaben.push({ wert, tokens });
    }
  }

  // Dedupe über den normalisierten Wert.
  return Array.from(new Map(angaben.map((a) => [stripSeparators(a.wert), a])).values());
}

/** Zeilen, die ein kritisches Muster treffen (für den Mehrzeiler-Rückfall). */
function extractCriticalLines(text: string): string[] {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => AI_CRITICAL_LINE_PATTERNS.some((pattern) => pattern.test(line)));

  return Array.from(new Map(lines.map((line) => [normalizeAiLine(line), line])).values());
}

/**
 * Prüft den KI-Vorschlag NACH der Antwort gegen das Original — je Tatsache, nicht
 * je Zeile (Begründung der Granularität siehe Block oben). Kritischer Verlust
 * (Fabrikat/Gleichwertigkeitsvorbehalt) sperrt die Übernahme, technischer Verlust
 * (Normen, Werte, Zahlen) warnt sichtbar.
 */
export function reviewAiTextProposal(original: string, proposal: string): PositionAiReview {
  const proposalLower = proposal.toLowerCase();
  const proposalStripped = stripSeparators(proposal);
  const normalizedProposal = normalizeAiLine(proposal);

  const fehlendeKritischeAngaben: string[] = [];

  // (1) Fabrikat/Typ — der Name muss überleben, nicht der Satz.
  for (const angabe of extractFabrikatsAngaben(original)) {
    const fehlend = angabe.tokens.filter((token) => !containsToken(proposalLower, token));
    if (fehlend.length > 0) {
      fehlendeKritischeAngaben.push(`Fabrikats-/Typangabe „${angabe.wert}"`);
    }
  }

  // (2) VOB-Gleichwertigkeitsvorbehalt — die Wendung muss vorhanden bleiben.
  if (GLEICHWERTIG_PATTERN.test(original) && !GLEICHWERTIG_PATTERN.test(proposal)) {
    fehlendeKritischeAngaben.push('Gleichwertigkeitsvorbehalt „oder gleichwertig"');
  }

  // (3) Rückfall für MEHRZEILIGE Texte: eine kritische Zeile, aus der sich keine
  // einzelne Tatsache extrahieren ließ, muss als Zeile überleben. Nur bei
  // Mehrzeilern — bei einzeiligen Texten wäre genau das der Fehlalarm von oben.
  const zeilen = original.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (zeilen.length > 1) {
    for (const zeile of extractCriticalLines(original)) {
      const hatEigeneTatsache =
        extractFabrikatsAngaben(zeile).length > 0 || GLEICHWERTIG_PATTERN.test(zeile);
      if (hatEigeneTatsache) continue;

      const normalizedLine = normalizeAiLine(zeile);
      if (normalizedLine.length > 0 && !normalizedProposal.includes(normalizedLine)) {
        fehlendeKritischeAngaben.push(`Pflichtzeile „${zeile}"`);
      }
    }
  }

  // (4) Normkennungen — trennzeichen-tolerant.
  const fehlendeNormen = uniqueMatches(original, NORM_PATTERN)
    .map((norm) => normalizeAiLine(norm).toUpperCase())
    .filter((norm) => !proposalStripped.includes(stripSeparators(norm)));

  // (5) Technische Werte-Tokens (24V, E30, IP 44).
  const fehlendeTechTokens = uniqueMatches(original, TECH_TOKEN_PATTERN)
    .map((token) => normalizeAiLine(token))
    .filter((token) => !proposalStripped.includes(stripSeparators(token)));

  const fehlendeTechnischeAngaben = Array.from(new Set([...fehlendeNormen, ...fehlendeTechTokens]));

  // (6) Zahlen/Zahlwörter. Ziffern, die bereits als Teil einer gemeldeten Norm
  // oder eines technischen Tokens auftauchen, NICHT doppelt listen.
  const bereitsGemeldeteZiffern = new Set(
    fehlendeTechnischeAngaben.flatMap((angabe) => uniqueMatches(angabe, ZAHL_TOKEN_PATTERN)),
  );

  const proposalDigits = new Set(
    uniqueMatches(proposal, ZAHL_TOKEN_PATTERN).map((token) => token.replace(',', '.')),
  );
  const fehlendeZahlen = uniqueMatches(original, ZAHL_TOKEN_PATTERN)
    .filter((token) => !proposalDigits.has(token.replace(',', '.')))
    .filter((token) => !bereitsGemeldeteZiffern.has(token));

  const fehlendeZahlwoerter = uniqueMatches(original, ZAHLWORT_PATTERN).filter(
    (wort) => !proposalLower.includes(wort),
  );

  const fehlendeZahlAngaben = [...fehlendeZahlen, ...fehlendeZahlwoerter];

  const verdict: PositionAiReview['verdict'] = fehlendeKritischeAngaben.length > 0
    ? 'abgelehnt'
    : (fehlendeTechnischeAngaben.length > 0 || fehlendeZahlAngaben.length > 0)
      ? 'warnung'
      : 'ok';

  return {
    verdict,
    fehlendeKritischeAngaben,
    fehlendeTechnischeAngaben,
    fehlendeZahlAngaben,
  };
}

// ---------------------------------------------------------------------------
// Antwort-Auswertung (leere/unvollständige Provider-Antwort ändert nichts)
// ---------------------------------------------------------------------------

/** Form der Antwort von POST /api/ai/rewrite (siehe client/src/lib/ai-text-service.ts). */
export interface AiRewriteResponseLike {
  success: boolean;
  rewritten?: string | null;
  error?: string;
  safeguarded?: boolean;
  note?: string;
}

export type PositionAiOutcome =
  | {
      /** Keine Änderung möglich — text IST das unangetastete Original (zeichengleich). */
      kind: 'unveraendert';
      text: string;
      grund: string;
    }
  | {
      kind: 'vorschlag';
      vorschlag: string;
      review: PositionAiReview;
      safeguarded: boolean;
      note?: string;
    };

export function resolveAiProposal(
  original: string,
  response: AiRewriteResponseLike,
): PositionAiOutcome {
  if (
    !response.success
    || typeof response.rewritten !== 'string'
    || response.rewritten.trim().length === 0
  ) {
    return {
      kind: 'unveraendert',
      text: original,
      grund: response.error || 'KI-Antwort war leer oder unvollständig — der Text bleibt unverändert.',
    };
  }

  return {
    kind: 'vorschlag',
    vorschlag: response.rewritten,
    review: reviewAiTextProposal(original, response.rewritten),
    safeguarded: response.safeguarded === true,
    note: response.note,
  };
}

/** Übernahme nur, wenn ein Vorschlag existiert UND keine kritische Pflichtangabe fehlt. */
export function darfVorschlagUebernommenWerden(outcome: PositionAiOutcome): boolean {
  return outcome.kind === 'vorschlag' && outcome.review.verdict !== 'abgelehnt';
}
